// Curated symptom-cluster map for the Phase-1 "suggest similar symptoms"
// widening behavior (PLAN.md §6.1.3.A, D-14). When the user reports a symptom,
// the assistant offers a single batched yes/no question about commonly
// co-occurring symptoms — it only ASKS; the user is the final say, and confirmed
// AI-suggested symptoms are flagged aiSuggested=true. `redflagSet` marks
// associated symptoms that, if present, contribute to a red-flag callout (§6.1.3.B).
//
// Phase 2 replaces this with per-patient learning; Phase 3 with SNOMED relations.

export interface SymptomCluster {
  /** Associated symptoms to ask about, in plain words. */
  askAbout: string[];
  /** Subset of askAbout that contributes to a red-flag combination. */
  redflagSet?: string[];
}

/** Keyed by a normalized symptom name (lowercase). */
export const SYMPTOM_CLUSTERS: Record<string, SymptomCluster> = {
  headache: {
    askAbout: [
      "nausea",
      "vomiting",
      "light sensitivity",
      "sound sensitivity",
      "smell sensitivity",
      "visual aura",
      "neck stiffness",
    ],
    redflagSet: ["neck stiffness"],
  },
  "chest pain": {
    askAbout: [
      "shortness of breath",
      "sweating",
      "pain spreading to the arm",
      "pain spreading to the jaw",
      "nausea",
      "lightheadedness",
    ],
    redflagSet: ["pain spreading to the arm", "pain spreading to the jaw", "sweating"],
  },
  nausea: {
    askAbout: ["vomiting", "diarrhea", "abdominal pain", "fever", "dizziness"],
  },
  diarrhea: {
    askAbout: ["nausea", "vomiting", "abdominal pain", "fever", "blood in stool", "dehydration"],
  },
  dizziness: {
    askAbout: ["room spinning", "lightheadedness", "ringing in ears", "hearing change", "balance problems", "headache"],
  },
  "abdominal pain": {
    askAbout: ["nausea", "vomiting", "fever", "diarrhea", "bloating", "rigid abdomen"],
    redflagSet: ["rigid abdomen", "fever"],
  },
  fatigue: {
    askAbout: ["poor sleep", "low mood", "shortness of breath", "fever", "muscle aches"],
  },
  cough: {
    askAbout: ["fever", "shortness of breath", "sore throat", "chest tightness", "mucus"],
  },
  "shortness of breath": {
    askAbout: ["chest tightness", "chest pain", "cough", "wheezing", "lightheadedness"],
    redflagSet: ["chest tightness", "chest pain"],
  },
  "sore throat": {
    askAbout: ["fever", "cough", "swollen glands", "difficulty swallowing", "runny nose"],
  },
  rash: {
    askAbout: ["itching", "fever", "swelling", "difficulty breathing", "spreading quickly"],
    redflagSet: ["difficulty breathing", "swelling"],
  },
  "back pain": {
    askAbout: ["numbness", "tingling", "leg weakness", "fever", "loss of bladder control"],
    redflagSet: ["loss of bladder control", "leg weakness"],
  },
  fever: {
    askAbout: ["chills", "body aches", "cough", "sore throat", "rash", "stiff neck"],
    redflagSet: ["stiff neck"],
  },
};

/** Normalizes a free-text symptom name for cluster lookup. */
export function normalizeSymptomName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Conservative synonym map: obvious lexical variants of the same complaint
 * (different spacing, informal words, adjective/noun form) → canonical form.
 * Intentionally excludes clinical near-neighbors (e.g. "chest pain" vs
 * "chest tightness") so a clinician reading grouped output isn't misled.
 * All keys must be pre-normalized (trim + lowercase).
 */
export const SYMPTOM_SYNONYMS: Record<string, string> = {
  // spacing / compound-word variants
  "stomachache":    "stomach ache",
  "bellyache":      "stomach ache",
  "belly ache":     "stomach ache",
  "belly pain":     "stomach pain",
  "tummy ache":     "stomach ache",
  "tummy pain":     "stomach pain",
  "backache":       "back pain",
  "back ache":      "back pain",
  "earache":        "ear pain",
  "ear ache":       "ear pain",
  // adjective → noun form
  "nauseous":       "nausea",
  "feverish":       "fever",
  // phrasing variants
  "lightheaded":       "lightheadedness",
  "light headed":      "lightheadedness",
  "light-headed":      "lightheadedness",
  "light-headedness":  "lightheadedness",
  "short of breath":   "shortness of breath",
  "breathlessness":    "shortness of breath",
  // side-effect-label wording vs. patient colloquial wording
  // (MEDICATION_SIDE_EFFECT_WARNING_PLAN.md D-SEW / Task 1.4)
  "drowsy":            "drowsiness",
  "somnolence":        "drowsiness",
  "sleepy":            "drowsiness",
  "dizzy":             "dizziness",
  "queasy":            "nausea",
  "trouble sleeping":  "insomnia",
  "itchy":             "itching",
  "itchy skin":        "itching",
  "pruritus":          "itching",
  "tired":             "fatigue",
};

/**
 * Resolve a pre-normalized symptom name to its canonical form.
 * Returns the input unchanged when no synonym mapping exists.
 */
export function resolveSymptomSynonym(normalized: string): string {
  return SYMPTOM_SYNONYMS[normalized] ?? normalized;
}

/** Compact lines describing the clusters, for embedding in the system prompt. */
export function clusterPromptLines(): string {
  return Object.entries(SYMPTOM_CLUSTERS)
    .map(([name, c]) => `    ${name}: ${c.askAbout.join(", ")}`)
    .join("\n");
}
